import { fetchCallsPlusTransactions } from "./callsPlus";
import type { OwnLedgerFeatures } from "./ownLedgerFeatures";
import { loadOwnLedgerFeatures } from "./ownLedgerFeatures";
import type { OptionPosition } from "./watchlistData";

export type LedgerSource = "stockforge" | "own_ledger";

let ownLedgerCacheEpoch = 0;
let ownLedgerRevisionMemo: { at: number; revision: string } | null = null;
let ownLedgerRevisionInflight: Promise<string> | null = null;
const ownLedgerGetCache = new Map<string, { revision: string; data: unknown }>();

/** Drop cached ledger reads after a write the health stamp does not cover. */
export function invalidateOwnLedgerGetCache(): void {
  ownLedgerCacheEpoch += 1;
  ownLedgerRevisionMemo = null;
  ownLedgerGetCache.clear();
}

async function ownLedgerFetch<T>(normalized: string, force: boolean): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  };
  if (force) headers["X-SFE-Refresh"] = "1";
  const res = await fetch(normalized, {
    credentials: "include",
    headers,
    cache: "no-store",
  });
  if (!res.ok) {
    const err = new Error(`Own ledger request failed (${res.status})`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

/** Cheap freshness token: last ledger write plus row counts, plus local invalidations. */
export async function ownLedgerReadRevision(): Promise<string> {
  if (ownLedgerRevisionMemo && Date.now() - ownLedgerRevisionMemo.at < 3000) {
    return ownLedgerRevisionMemo.revision;
  }
  if (!ownLedgerRevisionInflight) {
    ownLedgerRevisionInflight = ownLedgerFetch<{
      lastWriteAt?: string | null;
      transactionCount?: number;
      cashFlowCount?: number;
    }>("/own-ledger/health", false)
      .then((data) => {
        const revision = `${ownLedgerCacheEpoch}|${data.lastWriteAt ?? ""}|${Number(data.transactionCount) || 0}|${Number(data.cashFlowCount) || 0}`;
        ownLedgerRevisionMemo = { at: Date.now(), revision };
        return revision;
      })
      .finally(() => {
        ownLedgerRevisionInflight = null;
      });
  }
  return ownLedgerRevisionInflight;
}

export async function ownLedgerGet<T>(path: string, force = false): Promise<T> {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const normalized = suffix.startsWith("/own-ledger") ? suffix : `/own-ledger${suffix}`;
  const isHealth = normalized === "/own-ledger/health" || normalized.startsWith("/own-ledger/health?");
  if (!force && !isHealth) {
    const revision = await ownLedgerReadRevision();
    const hit = ownLedgerGetCache.get(normalized) as { revision: string; data: T } | undefined;
    if (hit && hit.revision === revision) return hit.data;
    const data = await ownLedgerFetch<T>(normalized, false);
    ownLedgerGetCache.set(normalized, { revision, data });
    return data;
  }
  if (force) ownLedgerRevisionMemo = null;
  return ownLedgerFetch<T>(normalized, force);
}

export async function loadOwnLedgerTransactionSnapshot(force = false) {
  return fetchCallsPlusTransactions(
    async <T>(snapshotPath: string, f?: boolean): Promise<T> => {
      if (snapshotPath.startsWith("/transactions-snapshot")) {
        return ownLedgerGet<T>("/transactions-snapshot", f ?? force);
      }
      return ownLedgerGet<T>(snapshotPath, f ?? force);
    },
    force,
  );
}

/**
 * Trades + cash flows for History(L) in one round trip.
 * Deleted cash rows stay on the server and are not part of this snapshot.
 */
export async function loadOwnLedgerHistoryBundle(force = false): Promise<{
  transactions: Awaited<ReturnType<typeof loadOwnLedgerTransactionSnapshot>>;
  cashFlows: OwnLedgerCashFlow[];
}> {
  const data = await ownLedgerGet<{
    transactions?: Record<string, unknown>[];
    cash_flows?: OwnLedgerCashFlow[];
  }>("/transactions-snapshot", force);
  const transactions = await fetchCallsPlusTransactions(
    async <T>(_path: string, _f?: boolean): Promise<T> =>
      ({ transactions: data.transactions ?? [] }) as T,
    force,
  );
  return { transactions, cashFlows: data.cash_flows ?? [] };
}

export interface OwnCapitalMonthCash {
  cashAdded: number;
  withdrawn: number;
  marginCost?: number;
  stockLendingIncome?: number;
  taxWithdrawn?: number;
}

/** Monthly cash added/withdrawn from own-ledger cash_flows table. */
export async function loadOwnLedgerCashMonthLedger(
  force = false,
  opts: { includeForecast?: boolean } = {},
): Promise<Record<string, OwnCapitalMonthCash>> {
  try {
    const qs = opts.includeForecast ? "?include_forecast=1" : "";
    const data = await ownLedgerGet<{ months?: Record<string, OwnCapitalMonthCash> }>(
      `/cash-flows/own-capital-months${qs}`,
      force,
    );
    return data.months ?? {};
  } catch {
    return {};
  }
}

export interface OwnLedgerCashFlow {
  id: number;
  flow_date: string;
  amount: number;
  direction: "in" | "out";
  category: string;
  broker: string | null;
  broker_id?: string | null;
  broker_name?: string | null;
  broker_ref: string | null;
  account_id: string | null;
  account_label?: string | null;
  account_name?: string | null;
  account_short_name?: string | null;
  source: string;
  note: string | null;
  /** Deleted by the user — kept as a tombstone so imports cannot revive it. */
  ignored?: boolean;
}

export interface LedgerBroker {
  broker_id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  account_count?: number;
}

export interface LedgerAccount {
  account_id: string;
  label: string;
  name?: string;
  broker: string;
  broker_id: string;
  broker_name?: string;
  broker_account_ref?: string | null;
  /** Narrow code shown in broker/account columns. */
  short_name?: string | null;
  created_at?: string;
  updated_at?: string;
}

export async function loadOwnLedgerHealth(force = false): Promise<{
  cashFlowCount: number;
  transactionCount: number;
  lastWriteAt: string | null;
}> {
  const data = await ownLedgerGet<{
    cashFlowCount?: number;
    transactionCount?: number;
    lastWriteAt?: string | null;
  }>("/health", force);
  return {
    cashFlowCount: Number(data.cashFlowCount) || 0,
    transactionCount: Number(data.transactionCount) || 0,
    lastWriteAt: typeof data.lastWriteAt === "string" && data.lastWriteAt ? data.lastWriteAt : null,
  };
}

export async function listOwnLedgerCashFlows(
  opts: { limit?: number; from?: string; to?: string } = {},
  force = false,
): Promise<OwnLedgerCashFlow[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.from) params.set("from", opts.from);
  if (opts.to) params.set("to", opts.to);
  const qs = params.toString();
  const data = await ownLedgerGet<{ cash_flows?: OwnLedgerCashFlow[] }>(
    `/cash-flows${qs ? `?${qs}` : ""}`,
    force,
  );
  return data.cash_flows ?? [];
}

export async function createOwnLedgerCashFlow(body: {
  flow_date: string;
  amount: number;
  direction: "in" | "out";
  category?: string;
  note?: string;
}): Promise<OwnLedgerCashFlow | null> {
  const res = await fetch("/own-ledger/cash-flows", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Create cash flow failed (${res.status})`);
  }
  const payload = (await res.json()) as { cash_flow?: OwnLedgerCashFlow };
  return payload.cash_flow ?? null;
}

export async function updateOwnLedgerCashFlow(
  id: number,
  body: { category?: string; note?: string | null },
): Promise<OwnLedgerCashFlow | null> {
  const res = await fetch(`/own-ledger/cash-flows/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Update cash flow failed (${res.status})`);
  }
  const payload = (await res.json()) as { cash_flow?: OwnLedgerCashFlow };
  return payload.cash_flow ?? null;
}

/**
 * Delete a cash flow, or put a deleted one back.
 *
 * The server hides the row instead of dropping it, so a deleted flow stays gone
 * after a reimport and can be restored later.
 */
export async function setOwnLedgerCashFlowDeleted(
  id: number,
  deleted: boolean,
): Promise<OwnLedgerCashFlow | null> {
  const res = await fetch(
    deleted ? `/own-ledger/cash-flows/${id}` : `/own-ledger/cash-flows/${id}/restore`,
    {
      method: deleted ? "DELETE" : "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!res.ok) {
    throw new Error(
      `${deleted ? "Delete" : "Restore"} cash flow failed (${res.status})`,
    );
  }
  const payload = (await res.json()) as { cash_flow?: OwnLedgerCashFlow };
  return payload.cash_flow ?? null;
}

export interface ForecastTaxCashFlowSyncRow {
  flow_date: string;
  amount: number;
  idempotency_key: string;
  note?: string | null;
}

/** Upsert forecast tax rows in cash_flows; removes stale forecast_tax source rows. */
export async function syncForecastTaxCashFlows(
  flows: ForecastTaxCashFlowSyncRow[],
): Promise<{ kept: number; deleted: number } | null> {
  const res = await fetch("/own-ledger/cash-flows/sync-forecast-tax", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ flows }),
  });
  if (!res.ok) {
    return null;
  }
  const payload = (await res.json()) as { kept?: number; deleted?: number };
  return {
    kept: Number(payload.kept) || 0,
    deleted: Number(payload.deleted) || 0,
  };
}

export interface OwnLedgerImportStatus {
  settings: {
    robinhoodStartDate: string;
    tastytradeEndDate: string;
    overlapDays: number;
    openingOwnCapital?: number;
    defaultTastytradeAccountId?: string;
    defaultRobinhoodAccountId?: string;
    defaultRobinhoodBrokerId?: string;
  };
  brokers?: LedgerBroker[];
  accounts?: LedgerAccount[];
}

export async function loadOwnLedgerImportStatus(force = false): Promise<OwnLedgerImportStatus | null> {
  try {
    const data = await ownLedgerGet<{
      settings?: OwnLedgerImportStatus["settings"];
      brokers?: LedgerBroker[];
      accounts?: LedgerAccount[];
    }>("/import/status", force);
    return {
      settings: data.settings ?? {
        robinhoodStartDate: "2025-10-01",
        tastytradeEndDate: "2025-12-16",
        overlapDays: 7,
        openingOwnCapital: 0,
      },
      brokers: data.brokers ?? [],
      accounts: data.accounts ?? [],
    };
  } catch {
    return null;
  }
}

async function ownLedgerJson<T>(
  path: string,
  init: RequestInit,
  fallback: string,
): Promise<T> {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const normalized = suffix.startsWith("/own-ledger") ? suffix : `/own-ledger${suffix}`;
  const res = await fetch(normalized, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  const payload = (await res.json().catch(() => null)) as
    | (T & { detail?: { message?: string } })
    | null;
  if (!res.ok) {
    throw new Error(payload?.detail?.message || `${fallback} (${res.status})`);
  }
  return (payload ?? {}) as T;
}

export interface LedgerTickerHolding {
  ticker: string;
  account_id: string;
  qty: number;
}

export async function loadLedgerCatalog(force = false): Promise<{
  brokers: LedgerBroker[];
  accounts: LedgerAccount[];
  holdings: LedgerTickerHolding[];
  active_account_ids: string[];
}> {
  const data = await ownLedgerGet<{
    brokers?: LedgerBroker[];
    accounts?: LedgerAccount[];
    holdings?: LedgerTickerHolding[];
    active_account_ids?: string[];
  }>("/brokers", force);
  return {
    brokers: data.brokers ?? [],
    accounts: data.accounts ?? [],
    holdings: data.holdings ?? [],
    active_account_ids: data.active_account_ids ?? [],
  };
}

export async function createLedgerBroker(name: string): Promise<LedgerBroker> {
  const payload = await ownLedgerJson<{ broker?: LedgerBroker }>(
    "/brokers",
    { method: "POST", body: JSON.stringify({ name }) },
    "Create broker failed",
  );
  if (!payload.broker) throw new Error("Create broker failed");
  return payload.broker;
}

export async function renameLedgerBroker(brokerId: string, name: string): Promise<LedgerBroker> {
  const payload = await ownLedgerJson<{ broker?: LedgerBroker }>(
    `/brokers/${encodeURIComponent(brokerId)}`,
    { method: "PATCH", body: JSON.stringify({ name }) },
    "Rename broker failed",
  );
  if (!payload.broker) throw new Error("Rename broker failed");
  return payload.broker;
}

export async function deleteLedgerBroker(brokerId: string): Promise<void> {
  await ownLedgerJson(`/brokers/${encodeURIComponent(brokerId)}`, { method: "DELETE" }, "Delete broker failed");
}

export async function createLedgerAccount(input: {
  name: string;
  brokerId: string;
}): Promise<LedgerAccount> {
  const payload = await ownLedgerJson<{ account?: LedgerAccount }>(
    "/accounts",
    { method: "POST", body: JSON.stringify({ name: input.name, broker_id: input.brokerId }) },
    "Create account failed",
  );
  if (!payload.account) throw new Error("Create account failed");
  return payload.account;
}

export async function renameLedgerAccount(
  accountId: string,
  input: { name?: string; brokerId?: string; shortName?: string },
): Promise<LedgerAccount> {
  const payload = await ownLedgerJson<{ account?: LedgerAccount }>(
    `/accounts/${encodeURIComponent(accountId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        name: input.name,
        broker_id: input.brokerId,
        short_name: input.shortName,
      }),
    },
    "Rename account failed",
  );
  if (!payload.account) throw new Error("Rename account failed");
  return payload.account;
}

export async function deleteLedgerAccount(accountId: string): Promise<void> {
  await ownLedgerJson(
    `/accounts/${encodeURIComponent(accountId)}`,
    { method: "DELETE" },
    "Delete account failed",
  );
}

export async function saveOwnLedgerImportSettings(patch: {
  defaultRobinhoodAccountId?: string;
  defaultRobinhoodBrokerId?: string;
}): Promise<OwnLedgerImportStatus["settings"]> {
  const payload = await ownLedgerJson<{ settings?: OwnLedgerImportStatus["settings"] }>(
    "/import/settings",
    { method: "PUT", body: JSON.stringify(patch) },
    "Save import settings failed",
  );
  return payload.settings ?? patch;
}

export interface OwnLedgerShareAvgHolding {
  /** Ledger account that owns this lot. Empty when the row predates account_id. */
  accountId: string;
  ticker: string;
  quantity: number;
  /** Strike-based avg (assignment lots use strike; premiums excluded). */
  avgCost: number;
  /** Premium-adjusted avg (assignment lots use strike − premiums). */
  adjustedAvg: number;
  costBasis: number;
  adjustedCostBasis: number;
  /** Calendar days since oldest FIFO open lot (own ledger). */
  daysHeld: number | null;
  /** Oldest open lot trade date YYYY-MM-DD. */
  openDate: string | null;
}

function parseShareAvgHolding(
  row: Partial<OwnLedgerShareAvgHolding> | null | undefined,
  accountId: string,
  ticker: string,
): OwnLedgerShareAvgHolding | null {
  const key = String(ticker ?? "").trim().toUpperCase();
  const avgCost = Number(row?.avgCost);
  const quantity = Number(row?.quantity);
  const adjustedAvg = Number(row?.adjustedAvg);
  const costBasis = Number(row?.costBasis);
  const adjustedCostBasis = Number(row?.adjustedCostBasis);
  if (!key || !(avgCost > 0) || !(quantity > 0)) return null;
  const adj = adjustedAvg > 0 ? adjustedAvg : avgCost;
  const daysHeldRaw = Number(row?.daysHeld);
  const openDate =
    typeof row?.openDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(row.openDate.slice(0, 10))
      ? row.openDate.slice(0, 10)
      : null;
  return {
    accountId: String(accountId ?? "").trim(),
    ticker: key,
    quantity,
    avgCost,
    adjustedAvg: adj,
    costBasis: costBasis > 0 ? costBasis : avgCost * quantity,
    adjustedCostBasis: adjustedCostBasis > 0 ? adjustedCostBasis : adj * quantity,
    daysHeld: Number.isFinite(daysHeldRaw) && daysHeldRaw >= 0 ? daysHeldRaw : null,
    openDate,
  };
}

async function shareAvgBooksInScope(
  books: OwnLedgerShareAvgHolding[],
): Promise<OwnLedgerShareAvgHolding[]> {
  const { scopeAllowsAccount } = await import("./accountScope");
  return books.filter((book) => scopeAllowsAccount(book.accountId));
}

export interface OwnLedgerOpenOption {
  optionSymbol: string;
  ticker: string;
  side: "put" | "call";
  strike: number;
  expDate: string;
  contracts: number;
  entryPrice: number;
  openCredit: number;
  openDate: string | null;
  daysHeld: number | null;
  optionId?: string | null;
  chainId?: string | null;
  accountId?: string | null;
  accountCount?: number;
  brokerId?: string | null;
  brokerName?: string | null;
  accountLabel?: string | null;
  accountShortName?: string | null;
}

/** Open shorts as position rows. Available shares use the call rows. */
export function ownLedgerOpenOptionsAsPositions(rows: OwnLedgerOpenOption[]): OptionPosition[] {
  return rows
    .filter((row) => row.contracts > 0)
    .map((row) => ({
      option_symbol: row.optionSymbol,
      ticker: row.ticker,
      strike: row.strike,
      side: row.side,
      exp_date: row.expDate,
      contracts: row.contracts,
      entry_price: row.entryPrice,
      open_credit: row.openCredit,
      open_date: row.openDate ?? undefined,
      expired: false,
      account_id: row.accountId ?? undefined,
      account_count: row.accountCount,
      broker_id: row.brokerId ?? undefined,
      broker_name: row.brokerName ?? undefined,
      account_label: row.accountLabel ?? undefined,
      account_short_name: row.accountShortName ?? undefined,
    }));
}

/** Call positions the Available column subtracts from shares held. */
export function ownLedgerCallPositions(rows: OwnLedgerOpenOption[]): OptionPosition[] {
  return ownLedgerOpenOptionsAsPositions(rows).filter((row) => row.side === "call");
}

/** Open short options from own ledger (STO − cover FIFO). */
export async function loadOwnLedgerOpenOptions(force = false): Promise<OwnLedgerOpenOption[]> {
  const features = await loadOwnLedgerFeatures(force);
  if (!features.enabled) return [];
  try {
    const data = await ownLedgerGet<{
      positions?: Array<Partial<OwnLedgerOpenOption>>;
    }>("/open-options", force);
    const out: OwnLedgerOpenOption[] = [];
    for (const row of data.positions ?? []) {
      const optionSymbol = String(row.optionSymbol ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      const ticker = String(row.ticker ?? "")
        .trim()
        .toUpperCase();
      const side = row.side === "call" || row.side === "put" ? row.side : null;
      const strike = Number(row.strike);
      const expDate = String(row.expDate ?? "").slice(0, 10);
      const contracts = Number(row.contracts);
      const entryPrice = Number(row.entryPrice);
      const openCredit = Number(row.openCredit);
      if (
        !optionSymbol ||
        !ticker ||
        !side ||
        !(strike > 0) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(expDate) ||
        !(contracts > 0) ||
        !(entryPrice >= 0)
      ) {
        continue;
      }
      const daysHeldRaw = Number(row.daysHeld);
      const optionId = String(row.optionId ?? "").trim();
      const chainId = String(row.chainId ?? "").trim();
      out.push({
        optionSymbol,
        ticker,
        side,
        strike,
        expDate,
        contracts,
        entryPrice,
        openCredit: openCredit > 0 ? openCredit : entryPrice * 100 * contracts,
        openDate:
          typeof row.openDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(row.openDate.slice(0, 10))
            ? row.openDate.slice(0, 10)
            : null,
        daysHeld: Number.isFinite(daysHeldRaw) && daysHeldRaw >= 0 ? daysHeldRaw : null,
        optionId: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(optionId)
          ? optionId
          : null,
        chainId: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(chainId)
          ? chainId
          : null,
        accountId: String(row.accountId ?? "").trim() || null,
        accountCount: Number(row.accountCount) > 1 ? Number(row.accountCount) : 1,
        brokerId: String(row.brokerId ?? "").trim() || null,
        brokerName: String(row.brokerName ?? "").trim() || null,
        accountLabel: String(row.accountLabel ?? "").trim() || null,
        accountShortName: String(row.accountShortName ?? "").trim() || null,
      });
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Open share avg cost, one book per ledger account + ticker.
 * Paper lots are included; apply Live/Paper scope before display.
 */
export async function fetchOwnLedgerShareAvgBooks(
  force = false,
): Promise<OwnLedgerShareAvgHolding[]> {
  const features = await loadOwnLedgerFeatures(force);
  if (!features.enabled) return [];
  const data = await ownLedgerGet<{
    books?: Array<Partial<OwnLedgerShareAvgHolding> & { accountId?: string; ticker?: string }>;
    holdings?: Record<string, Partial<OwnLedgerShareAvgHolding> & { accountId?: string }>;
  }>("/share-avg-cost", force);
  const books: OwnLedgerShareAvgHolding[] = [];
  if (Array.isArray(data.books) && data.books.length) {
    for (const row of data.books) {
      const parsed = parseShareAvgHolding(row, String(row.accountId ?? ""), String(row.ticker ?? ""));
      if (parsed) books.push(parsed);
    }
    return books;
  }
  for (const [key, row] of Object.entries(data.holdings ?? {})) {
    const sep = key.indexOf("|");
    const accountId =
      String(row?.accountId ?? "").trim() || (sep > 0 ? key.slice(0, sep) : "");
    const ticker = sep > 0 ? key.slice(sep + 1) : key;
    const parsed = parseShareAvgHolding(row, accountId === "_" ? "" : accountId, ticker);
    if (parsed) books.push(parsed);
  }
  return books;
}

export async function loadOwnLedgerShareAvgBooks(
  force = false,
): Promise<OwnLedgerShareAvgHolding[]> {
  try {
    return await fetchOwnLedgerShareAvgBooks(force);
  } catch {
    return [];
  }
}

/** Scoped books for the current Live/Paper + account picker. Never mixes paper into live. */
export async function loadScopedShareAvgBooks(force = false): Promise<OwnLedgerShareAvgHolding[]> {
  return shareAvgBooksInScope(await loadOwnLedgerShareAvgBooks(force));
}

/**
 * Largest in-scope book per ticker (no blending across accounts).
 */
export async function fetchOwnLedgerShareAvgByTicker(
  force = false,
): Promise<Map<string, OwnLedgerShareAvgHolding>> {
  const map = new Map<string, OwnLedgerShareAvgHolding>();
  const books = await shareAvgBooksInScope(await fetchOwnLedgerShareAvgBooks(force));
  for (const book of books) {
    const prev = map.get(book.ticker);
    if (!prev || book.quantity > prev.quantity) map.set(book.ticker, book);
  }
  return map;
}

/** Same holdings, but an unavailable ledger reads as empty rather than throwing. */
export async function loadOwnLedgerShareAvgByTicker(
  force = false,
): Promise<Map<string, OwnLedgerShareAvgHolding>> {
  try {
    return await fetchOwnLedgerShareAvgByTicker(force);
  } catch {
    return new Map<string, OwnLedgerShareAvgHolding>();
  }
}

export function shareAvgForAccountTicker(
  books: readonly OwnLedgerShareAvgHolding[],
  ticker: string,
  accountId?: string | null,
): number | undefined {
  const key = String(ticker ?? "").trim().toUpperCase();
  if (!key) return undefined;
  const matches = books.filter((book) => book.ticker === key && book.avgCost > 0);
  const account = String(accountId ?? "").trim();
  if (account) {
    const exact = matches.find((book) => book.accountId === account);
    if (exact) return exact.avgCost;
  }
  const best = [...matches].sort((a, b) => b.quantity - a.quantity)[0];
  return best?.avgCost;
}

/** Strike-based avg cost map — largest in-scope book per ticker (not blended).
 * Also stores `${accountId}|${ticker}` so option rows can use that account's avg.
 */
export async function loadOwnLedgerStrikeAvgMap(force = false): Promise<Map<string, number>> {
  const books = await loadScopedShareAvgBooks(force);
  const map = new Map<string, number>();
  const best = new Map<string, OwnLedgerShareAvgHolding>();
  for (const book of books) {
    if (!(book.avgCost > 0)) continue;
    if (book.accountId) map.set(`${book.accountId}|${book.ticker}`, book.avgCost);
    const prev = best.get(book.ticker);
    if (!prev || book.quantity > prev.quantity) best.set(book.ticker, book);
  }
  for (const [ticker, book] of best) map.set(ticker, book.avgCost);
  return map;
}

/** Open stock KPI summary for Analytics(L): own-ledger strike cost + RH MCP marks. */
export async function loadOwnLedgerStockOpenSummary(
  force = false,
  allowTicker?: (ticker: string) => boolean,
): Promise<{
  count: number;
  quantity: number;
  cost_basis: number;
  market_value: number;
  open_pl: number;
  open_pl_pct: number | null;
}> {
  const empty = {
    count: 0,
    quantity: 0,
    cost_basis: 0,
    market_value: 0,
    open_pl: 0,
    open_pl_pct: null as number | null,
  };
  try {
    const holdings = await loadScopedShareAvgBooks(force);
    if (!holdings.length) return empty;

    const openRows: Array<{ ticker: string; qty: number; basis: number }> = [];
    for (const row of holdings) {
      if (allowTicker && !allowTicker(row.ticker)) continue;
      const qty = Number(row.quantity) || 0;
      const avgCost = Number(row.avgCost) || 0;
      if (!(qty > 0) || !(avgCost > 0)) continue;
      openRows.push({
        ticker: row.ticker,
        qty,
        basis: row.costBasis > 0 ? row.costBasis : avgCost * qty,
      });
    }
    if (!openRows.length) return empty;

    const { fetchAgenticEquityQuotes } = await import("../api/agenticClient");
    const priceByTicker = await fetchAgenticEquityQuotes(openRows.map((row) => row.ticker)).catch(
      () => new Map<string, number>(),
    );

    let count = 0;
    let quantity = 0;
    let costBasis = 0;
    let marketValue = 0;
    let pricedCostBasis = 0;

    for (const row of openRows) {
      count += 1;
      quantity += row.qty;
      costBasis += row.basis;
      const price = priceByTicker.get(row.ticker);
      if (price != null && price > 0) {
        marketValue += price * row.qty;
        pricedCostBasis += row.basis;
      }
    }

    const openPl = marketValue - pricedCostBasis;
    const openPlPct = pricedCostBasis > 0 ? (openPl / pricedCostBasis) * 100 : null;
    return {
      count,
      quantity,
      cost_basis: costBasis,
      market_value: marketValue,
      open_pl: openPl,
      open_pl_pct: openPlPct,
    };
  } catch {
    return empty;
  }
}

export async function dualWriteCreateLedgerTransaction(
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch("/own-ledger/dual-write/transactions", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseDualWriteResponse(res, "Dual-write failed");
}

async function parseDualWriteResponse(
  res: Response,
  fallbackMessage: string,
): Promise<Record<string, unknown>> {
  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("json")
    ? ((await res.json().catch(() => null)) as Record<string, unknown> | null)
    : null;
  if (res.ok) {
    const stockforge = payload?.stockforge;
    if (stockforge && typeof stockforge === "object") {
      return stockforge as Record<string, unknown>;
    }
    return payload ?? {};
  }
  const detail =
    payload && typeof payload === "object" && payload.detail && typeof payload.detail === "object"
      ? (payload.detail as { message?: string; code?: string })
      : null;
  const err = new Error(detail?.message?.trim() || `${fallbackMessage} (${res.status})`) as Error & {
    code?: string;
  };
  err.code = detail?.code;
  throw err;
}

export async function dualWriteUpdateLedgerTransaction(
  sfId: number,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`/own-ledger/dual-write/transactions/${sfId}`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseDualWriteResponse(res, "Dual-write update failed");
}

/** History(L) edits pass own-ledger ids; server resolves sf_transaction_id when linked. */
export async function dualWriteUpdateLedgerTransactionByOwnId(
  ownId: number,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`/own-ledger/dual-write/transactions/by-own-id/${ownId}`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseDualWriteResponse(res, "Dual-write update failed");
}

export async function dualWriteAssignmentPairLedger(
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch("/own-ledger/dual-write/transactions/assignment-pair", {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseDualWriteResponse(res, "Dual-write assignment-pair failed");
}

/** Link assigned put → assigned call (Analytics(L) wheel-pair assign P/L). */
export async function saveOwnLedgerTransactionNote(id: number, body: string): Promise<string> {
  const res = await fetch(`/own-ledger/transactions/${id}/note`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    note?: string;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Save note failed (${res.status})`);
  }
  return String(payload.note ?? "").trim();
}

/** Move a trade (and assignment-linked legs) to another ledger account / broker. */
export async function reassignOwnLedgerTransactionAccount(
  id: number,
  accountId: string,
): Promise<{ updated: boolean; updatedIds: number[] }> {
  const res = await fetch(`/own-ledger/transactions/${id}/account`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ account_id: accountId }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    updated?: boolean;
    updated_ids?: number[];
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Change account failed (${res.status})`);
  }
  return {
    updated: payload.updated !== false,
    updatedIds: Array.isArray(payload.updated_ids)
      ? payload.updated_ids.map((value) => Number(value)).filter((value) => value > 0)
      : [id],
  };
}

/** Move a cash flow to another ledger account / broker. */
export async function reassignOwnLedgerCashFlowAccount(
  id: number,
  accountId: string,
): Promise<{ updated: boolean }> {
  const res = await fetch(`/own-ledger/cash-flows/${id}/account`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ account_id: accountId }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    updated?: boolean;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Change account failed (${res.status})`);
  }
  return { updated: payload.updated !== false };
}

export async function linkWheelPairPutCall(putId: number, callId: number): Promise<void> {
  const res = await fetch(`/own-ledger/transactions/${putId}/wheel-pair`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ call_id: callId }),
  });
  const data = (await res.json().catch(() => ({}))) as { detail?: { message?: string } };
  if (!res.ok) {
    throw new Error(data.detail?.message ?? "Failed to link wheel pair");
  }
}

export async function unlinkWheelPairPutCall(putId: number, callId: number): Promise<void> {
  const res = await fetch(`/own-ledger/transactions/${putId}/wheel-pair/${callId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = (await res.json().catch(() => ({}))) as { detail?: { message?: string } };
  if (!res.ok) {
    throw new Error(data.detail?.message ?? "Failed to unlink wheel pair");
  }
}

export async function dualWriteBatchDeleteLedgerTransactions(
  ids: number[],
): Promise<Record<string, unknown>> {
  const res = await fetch("/own-ledger/dual-write/transactions/batch-delete", {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  return parseDualWriteResponse(res, "Dual-write delete failed");
}

export async function dualWriteResolveLedgerPosition(
  optionSymbol: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await fetch(
    `/own-ledger/dual-write/positions/${encodeURIComponent(optionSymbol)}/resolve`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return parseDualWriteResponse(res, "Dual-write resolve failed");
}

export interface WebullPaperStatus {
  ok: boolean;
  connected: boolean;
  serverKeysAvailable?: boolean;
  appKeyHint?: string;
  lastSyncAt?: string | null;
  refreshMinutes?: number;
  importSummary?: { trades?: number; cash?: number } | null;
  accounts: LedgerAccount[];
}

export async function loadWebullPaperStatus(): Promise<WebullPaperStatus> {
  return ownLedgerJson<WebullPaperStatus>("/webull/paper", { method: "GET" }, "Webull paper status failed");
}

export async function connectWebullPaper(input: {
  appKey?: string;
  appSecret?: string;
  useServerKeys?: boolean;
}): Promise<WebullPaperStatus> {
  return ownLedgerJson<WebullPaperStatus>(
    "/webull/paper/connect",
    { method: "POST", body: JSON.stringify(input) },
    "Webull paper connect failed",
  );
}

export async function syncWebullPaper(): Promise<WebullPaperStatus> {
  return ownLedgerJson<WebullPaperStatus>(
    "/webull/paper/sync",
    { method: "POST", body: JSON.stringify({}) },
    "Webull paper sync failed",
  );
}

export async function disconnectWebullPaper(): Promise<WebullPaperStatus> {
  return ownLedgerJson<WebullPaperStatus>(
    "/webull/paper",
    { method: "DELETE" },
    "Webull paper disconnect failed",
  );
}

export { loadOwnLedgerFeatures };
export type { OwnLedgerFeatures };
